import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { T, useGT } from 'gt-react';
import { partitionMatchableInvoiceLineItems } from '@erp/shared-utils';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useLocalStorageValue } from '@react-hookz/web';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { InvoiceMetadata } from '@/components/accounts-payable/invoice-detail/invoice-metadata';
import { useInvoiceSlideinActionsSlot } from '@/components/accounts-payable/invoice-detail/invoiceSlideinActionsContext';
import { InvoiceLineItems } from '@/components/accounts-payable/invoice-detail/invoice-line-items';
import { RelatedDocumentLines } from '@/components/accounts-payable/invoice-detail/RelatedDocumentLines';
import { RelatedLinesTabLabel } from '@/components/accounts-payable/invoice-detail/RelatedLinesTabLabel';
import { useInvoiceDetailTab } from '@/components/accounts-payable/invoice-detail/useInvoiceDetailTab';
import { SplitProposalReviewContext } from '@/components/accounts-payable/invoice-detail/splitProposalReviewContext';
import { InvoiceNotesActivityTabs } from '@/components/accounts-payable/invoice-detail/InvoiceNotesActivityTabs';
import { InvoiceExceptionsSection } from '@/components/accounts-payable/exceptions/InvoiceExceptionsSection';
import { InvoiceTasksSection } from '@/components/accounts-payable/invoice-detail/InvoiceTasksSection';
import { SupportingDocumentsSection } from '@/components/accounts-payable/invoice-detail/supporting-documents-section';
import { FamousVouchersSection } from '@/components/accounts-payable/invoice-detail/FamousVouchersSection';
import { ChargesTable } from '@/components/common/charges-table';
import { SmartEmailViewer } from '@/components/email-viewer/SmartEmailViewer';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { MemoryBankNotes } from '@/components/memory-bank/MemoryBankNotes';
import { InvoiceSnoozeActions } from '@/components/accounts-payable/snooze/InvoiceSnoozeActions';
import { useChargesForEntity } from '@/api/rpc/hooks/finance/useCharges';
import type {
  APInvoice,
  APInvoiceRelatedConversation,
  GetAPInvoiceResponse,
  InboundEmail,
} from '@erp/rpc';
import { extractedTotalWarningMessages } from '@/components/accounts-payable/invoice-detail/extractedTotalWarning';
import { AIWorkflow, APInvoiceStatus, SystemEntity } from '@erp/shared-types';
import { SyncToERPButton } from '@/components/accounts-payable/invoice-detail/integrations';
import { useAcceptAPInvoice } from '@/api/rpc/hooks/accounts-payable/useAcceptAPInvoice';
import { useUnacceptAPInvoice } from '@/api/rpc/hooks/accounts-payable/useUnacceptAPInvoice';
import { useCreatePaymentForAPInvoice } from '@/api/rpc/hooks/accounts-payable/useCreatePaymentForAPInvoice';
import { Can } from '@/context/authz';
import { Action, Subject } from '@erp/authz';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { parseDateOnly } from '@/utils/dateUtils';
import { CalendarIcon, InfoIcon } from '@phosphor-icons/react';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { useUpdateAPInvoice } from '@/api/rpc/hooks/accounts-payable/useUpdateAPInvoice';
import { format } from 'date-fns';
import { TroubleDataAlert } from '@/components/common/TroubleDataBadges';
import { useAPInvoiceHolds } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import {
  acceptWarnings,
  summarizeInvoiceHolds,
  type AcceptWarning,
} from '@/components/accounts-payable/shared/match-display-state';
import {
  approvalBlocksIncludeMatchGaps,
  invoiceApprovalBlocks,
} from '@/components/accounts-payable/shared/invoiceApprovalBlocks';
import { ApprovalBlockList } from '@/components/accounts-payable/invoice-detail/ApprovalBlockList';
import { CannotApproveInvoicesDialog } from '@/components/accounts-payable/invoice-detail/CannotApproveInvoicesDialog';
export interface InvoiceDetailsProps {
  invoice: APInvoice;
  inboundEmail?: InboundEmail | null;
  lineItems?: Array<{
    id: string;
    lineNumber: number;
    description?: string | null;
    quantity?: string | number | null;
    unitPrice?: string | null;
    totalPrice?: string | null;
    taxAmount?: string | null;
  }>;
  /**
   * Every email conversation that contributed to this invoice — the original
   * source thread plus any later threads whose extraction triggered an
   * `arbitrateAPInvoiceUpdate` run. When multiple conversations are present
   * the right-side panel renders them stacked.
   */
  relatedConversations?: APInvoiceRelatedConversation[];
  /**
   * Set when the line-item total is not the total extracted from the
   * document. Shown under Total Amount. Does not affect Accept.
   */
  extractedTotalWarning?: GetAPInvoiceResponse['extractedTotalWarning'];
}

export function InvoiceDetails({
  invoice,
  inboundEmail,
  lineItems,
  relatedConversations = [],
  extractedTotalWarning = null,
}: InvoiceDetailsProps) {
  const { value: panelSize, set: setPanelSize } = useLocalStorageValue<number>(
    'invoice-details-panel-size',
    {
      defaultValue: 66,
      initializeWithValue: true,
    },
  );

  const actionsSlot = useInvoiceSlideinActionsSlot();

  // Fetch charges for the invoice (only header charges)
  const { charges: allCharges } = useChargesForEntity({
    entityType: SystemEntity.AP_INVOICE,
    entityId: invoice.id,
    includeLineCharges: false,
  });

  // Filter to only header charges (no docLineId)
  const headerCharges = useMemo(
    () => allCharges.filter((charge) => !charge.docLineId),
    [allCharges],
  );

  const acceptInvoice = useAcceptAPInvoice();
  const unacceptInvoice = useUnacceptAPInvoice();
  const createPayment = useCreatePaymentForAPInvoice();

  const updateInvoice = useUpdateAPInvoice();

  const holdsQuery = useAPInvoiceHolds(invoice.id);
  const onHoldSummary = summarizeInvoiceHolds(holdsQuery.holds);
  const [confirmAcceptOpen, setConfirmAcceptOpen] = useState(false);
  const [matchBlockOpen, setMatchBlockOpen] = useState(false);
  const [confirmUnacceptOpen, setConfirmUnacceptOpen] = useState(false);
  const [confirmCreatePaymentOpen, setConfirmCreatePaymentOpen] =
    useState(false);

  // Editable scheduled date for the approval confirmation dialog
  // Defaults to scheduled payment date, then invoice date
  const defaultConfirmDate = useMemo(() => {
    if (invoice.scheduledPaymentDate) {
      return new Date(invoice.scheduledPaymentDate);
    }
    return parseDateOnly(invoice.invoiceDate) ?? new Date();
  }, [invoice.scheduledPaymentDate, invoice.invoiceDate]);

  const [confirmScheduledDate, setConfirmScheduledDate] = useState<
    Date | undefined
  >(defaultConfirmDate);

  // Informational rows (qty=0 AND unitPrice=0 — e.g. seal numbers, container
  // refs, notes) are hidden by default since they can't match anything and
  // just add noise. The user can opt-in to seeing them via the toggle below.
  // Uses the same predicate the backend matcher and match-summary aggregate
  // use — see `packages/shared-utils/src/invoice-line-items.ts`.
  const [showMetadataRows, setShowMetadataRows] = useState(false);
  const { matchableLineItems, informationalLineItems } = useMemo(() => {
    const { matchable, informational } = partitionMatchableInvoiceLineItems(
      lineItems ?? [],
    );
    return {
      matchableLineItems: matchable,
      informationalLineItems: informational,
    };
  }, [lineItems]);
  // Memoize so the reference handed to `InvoiceLineItems` is stable across
  // renders.
  const displayLineItems = useMemo(
    () =>
      showMetadataRows
        ? [...matchableLineItems, ...informationalLineItems]
        : matchableLineItems,
    [showMetadataRows, matchableLineItems, informationalLineItems],
  );

  const isDraft = invoice.status === APInvoiceStatus.DRAFT;
  const isMatched = invoice.status === APInvoiceStatus.MATCHED;
  const canEditMatches = isDraft || isMatched;
  const tab = useInvoiceDetailTab(
    invoice.id,
    lineItems && lineItems.length > 0 ? 'line-items' : 'charges',
  );
  const isAccepted = invoice.status === APInvoiceStatus.ACCEPTED;
  const isFinalized = invoice.status === APInvoiceStatus.FINALIZED;

  // Soft hold: people can still accept, but the confirmation warns first.
  // Auto-accept skips these (APInvoiceService.acceptMatches). An invoice
  // that is not fully matched never reaches that confirmation.
  const onPaymentHold = invoice.paymentHold;
  const matchedOnHoldPay = holdsQuery.holds.some(
    (hold) => hold.matched && hold.value === 'HOLD_PAY',
  );
  const approvalBlocks = invoiceApprovalBlocks(invoice);
  const shouldShowAcceptButton =
    (isDraft || isMatched) && !isFinalized && !isAccepted;
  const shouldShowCreatePaymentButton = isAccepted;
  const shouldShowUnacceptButton = isAccepted && !isFinalized;

  const gt = useGT();
  const extractedTotalNotes = extractedTotalWarningMessages(
    extractedTotalWarning,
    gt,
  );

  const acceptWarningText: Record<AcceptWarning, string> = {
    PAYMENT_HOLD: gt('This invoice is on payment hold.', {
      context: 'accept-invoice confirmation warning',
    }),
    MATCHED_HOLD_PAY: gt('Matched lines are on Hold Pay in the source ERP.', {
      context: 'accept-invoice confirmation warning',
    }),
    HOLDS_LOADING: gt(
      'Holds on its POs, orders or loads are still being checked.',
      {
        context:
          'accept-invoice confirmation while the source-ERP hold lookup is loading',
      },
    ),
    REVIEW_HOLDS: gt('Review the holds below.', {
      context:
        'accept-invoice confirmation; the dialog lists holds on linked documents',
    }),
  };
  const acceptWarningMessages = acceptWarnings({
    onPaymentHold,
    matchedOnHoldPay,
    holdsLoading: holdsQuery.isPending,
    holdsFailed: holdsQuery.isError,
    onHoldCount: onHoldSummary.onHoldCount,
  }).map((warning) => acceptWarningText[warning]);
  const confirmAcceptMessage =
    acceptWarningMessages.length > 0
      ? `${acceptWarningMessages.join(' ')} ${gt('Accept anyway?')}`
      : null;

  function handleAccept() {
    if (acceptInvoice.isPending) return;
    if (approvalBlocks.length > 0) {
      setMatchBlockOpen(true);
      return;
    }
    setConfirmAcceptOpen(true);
  }

  function handleConfirmAccept() {
    if (approvalBlocks.length > 0 || acceptInvoice.isPending) return;
    setConfirmAcceptOpen(false);
    acceptInvoice.mutate({ id: invoice.id });
  }

  function handleUnaccept() {
    if (!shouldShowUnacceptButton || unacceptInvoice.isPending) return;
    setConfirmUnacceptOpen(true);
  }

  function handleConfirmUnaccept() {
    if (!shouldShowUnacceptButton || unacceptInvoice.isPending) return;
    setConfirmUnacceptOpen(false);
    unacceptInvoice.mutate({ id: invoice.id });
  }

  function handleCreatePayment() {
    if (!shouldShowCreatePaymentButton || createPayment.isPending) return;
    setConfirmScheduledDate(defaultConfirmDate);
    setConfirmCreatePaymentOpen(true);
  }

  async function handleConfirmCreatePayment() {
    if (createPayment.isPending || updateInvoice.isPending) return;

    const existingDateStr = invoice.scheduledPaymentDate?.slice(0, 10) ?? null;
    const newDateStr = confirmScheduledDate
      ? format(confirmScheduledDate, 'yyyy-MM-dd')
      : null;
    const dateChanged = existingDateStr !== newDateStr;

    if (dateChanged && confirmScheduledDate) {
      try {
        await updateInvoice.mutateAsync({
          id: invoice.id,
          scheduledPaymentDate: confirmScheduledDate.toISOString(),
          scheduledPaymentExplanation: gt('Confirmed at payment creation'),
        });
      } catch {
        return;
      }
    }

    setConfirmCreatePaymentOpen(false);
    createPayment.mutate({ id: invoice.id });
  }

  const sourceDocumentId =
    invoice.source === 'document' ? (invoice.sourceId ?? undefined) : undefined;
  const sourceEmailId = inboundEmail?.id ?? undefined;

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full gap-2 px-3">
      <ResizablePanel
        defaultSize={panelSize}
        onResize={(size) => setPanelSize(size)}
        minSize={25}
        className="flex flex-col pr-2"
      >
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
          <InvoiceMetadata
            invoice={invoice}
            extractedTotalNotes={extractedTotalNotes}
            headerActions={
              <>
                <InvoiceSnoozeActions invoice={invoice} />
                <MemoryBankNotes
                  staticMetadata={{
                    workflow: AIWorkflow.AP_INVOICE_INGESTION,
                    workflowStep: 'AP_INVOICE_INGESTION',
                  }}
                  entityId={invoice.id}
                  entityType={SystemEntity.AP_INVOICE}
                  tooltipText={gt(
                    'Add notes to guide AP invoice extraction and matching for this invoice.',
                  )}
                />
              </>
            }
            attention={
              <>
                <InvoiceExceptionsSection invoiceId={invoice.id} />
                <InvoiceTasksSection invoiceId={invoice.id} />
              </>
            }
          />

          <TroubleDataAlert
            onHoldCount={onHoldSummary.onHoldCount}
            holdSources={onHoldSummary.holdSources}
            checkFailed={holdsQuery.isError}
            className="mx-1"
          />

          {/* Tabs for Line Items and Charges */}
          {(lineItems && lineItems.length > 0) || headerCharges.length > 0 ? (
            <>
              <Separator />
              <Tabs
                value={tab.value}
                onValueChange={tab.onValueChange}
                className="w-full"
              >
                <TabsList className="w-full">
                  {lineItems && lineItems.length > 0 && (
                    <TabsTrigger value="line-items">
                      <T>Line Items</T> ({matchableLineItems.length})
                    </TabsTrigger>
                  )}
                  {lineItems && lineItems.length > 0 && (
                    <TabsTrigger value="related-lines">
                      <RelatedLinesTabLabel lineItems={matchableLineItems} />
                    </TabsTrigger>
                  )}
                  {headerCharges.length > 0 && (
                    <TabsTrigger value="charges">
                      <T>Charges</T> ({headerCharges.length})
                    </TabsTrigger>
                  )}
                </TabsList>
                {lineItems && lineItems.length > 0 && (
                  <TabsContent value="line-items" className="mt-0">
                    {informationalLineItems.length > 0 && (
                      <div className="flex items-center justify-end gap-2 px-3 pb-2">
                        <Switch
                          id="show-metadata-rows"
                          checked={showMetadataRows}
                          onCheckedChange={setShowMetadataRows}
                        />
                        <Label
                          htmlFor="show-metadata-rows"
                          className="text-xs text-muted-foreground font-normal cursor-pointer"
                        >
                          <T context="toggle label above the AP invoice line-items table; reveals rows with zero quantity and zero unit price (e.g. seal numbers, container refs, notes)">
                            Show metadata rows
                          </T>{' '}
                          ({informationalLineItems.length})
                        </Label>
                      </div>
                    )}
                    <SplitProposalReviewContext.Provider
                      value={tab.openRelatedLines}
                    >
                      <InvoiceLineItems
                        invoiceId={invoice.id}
                        lineItems={displayLineItems}
                        currency={invoice.currency}
                        canEditMatches={canEditMatches}
                        supplierId={invoice.supplierId}
                      />
                    </SplitProposalReviewContext.Provider>
                  </TabsContent>
                )}
                {lineItems && lineItems.length > 0 && (
                  <TabsContent value="related-lines" className="mt-0">
                    <RelatedDocumentLines
                      invoiceId={invoice.id}
                      lineItems={matchableLineItems}
                      canEditMatches={canEditMatches}
                      invoiceTotal={invoice.totalAmount}
                      invoiceSupplierId={invoice.supplierId}
                      currency={invoice.currency}
                    />
                  </TabsContent>
                )}
                {headerCharges.length > 0 && (
                  <TabsContent value="charges" className="mt-0">
                    <div className="px-3">
                      <ChargesTable
                        charges={headerCharges}
                        currency={invoice.currency ?? 'USD'}
                      />
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </>
          ) : null}

          <SupportingDocumentsSection apInvoiceId={invoice.id} />

          <FamousVouchersSection
            invoiceId={invoice.id}
            currency={invoice.currency}
          />

          <InvoiceNotesActivityTabs invoiceId={invoice.id} />
        </div>

        {actionsSlot &&
          createPortal(
            <>
              <SyncToERPButton invoice={invoice} />

              {shouldShowUnacceptButton && (
                <Can I={Action.Approve} a={Subject.APInvoice}>
                  <Button
                    variant="outline"
                    size="default"
                    disabled={unacceptInvoice.isPending}
                    onClick={handleUnaccept}
                  >
                    {unacceptInvoice.isPending ? (
                      <T>Reverting...</T>
                    ) : (
                      <T>Revert to Draft</T>
                    )}
                  </Button>
                </Can>
              )}

              <ConfirmDialog
                open={confirmUnacceptOpen}
                onClose={() => setConfirmUnacceptOpen(false)}
                onConfirm={handleConfirmUnaccept}
                title={gt('Revert to Draft?')}
                message={gt(
                  'This returns the invoice to Draft so its matches can be reviewed and edited again. Existing matches are kept; the acceptance is cleared.',
                )}
                confirmText={gt('Revert to Draft')}
                cancelText={gt('Cancel')}
                loading={unacceptInvoice.isPending}
              />

              {shouldShowCreatePaymentButton && (
                <Can I={Action.Approve} a={Subject.APInvoice}>
                  <Button
                    variant="default"
                    size="default"
                    disabled={createPayment.isPending}
                    onClick={handleCreatePayment}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {createPayment.isPending ? (
                      <T>Creating Payment...</T>
                    ) : (
                      <T>Create Payment</T>
                    )}
                  </Button>
                </Can>
              )}

              <ConfirmDialog
                open={confirmCreatePaymentOpen}
                onClose={() => setConfirmCreatePaymentOpen(false)}
                onConfirm={handleConfirmCreatePayment}
                title={gt('Create Payment')}
                confirmText={gt('Create Payment')}
                cancelText={gt('Cancel')}
                loading={createPayment.isPending || updateInvoice.isPending}
              >
                <div className="space-y-3">
                  <TroubleDataAlert
                    onHoldCount={onHoldSummary.onHoldCount}
                    holdSources={onHoldSummary.holdSources}
                    checkFailed={holdsQuery.isError}
                  />
                  <div className="rounded-md border p-3 bg-muted/50">
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <CalendarIcon className="h-4 w-4" />
                      <T>Scheduled Payment Date</T>
                    </div>
                    <DatePicker
                      value={confirmScheduledDate}
                      onSelect={setConfirmScheduledDate}
                      placeholder={gt('Select scheduled date')}
                    />
                    {invoice.scheduledPaymentExplanation && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1 mb-1">
                          <InfoIcon className="h-3 w-3" />
                          <T>Calculation Details</T>
                        </div>
                        <pre className="whitespace-pre-wrap font-sans">
                          {invoice.scheduledPaymentExplanation}
                        </pre>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <T>A payment will be scheduled for this date.</T>
                  </p>
                </div>
              </ConfirmDialog>

              <Can I={Action.Approve} a={Subject.APInvoice}>
                {shouldShowAcceptButton && (
                  <Button
                    variant="default"
                    size="default"
                    disabled={acceptInvoice.isPending}
                    onClick={handleAccept}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {acceptInvoice.isPending ? (
                      <T>Accepting...</T>
                    ) : (
                      <T>Accept Matches</T>
                    )}
                  </Button>
                )}
              </Can>
            </>,
            actionsSlot,
          )}

        <CannotApproveInvoicesDialog
          open={matchBlockOpen}
          onClose={() => setMatchBlockOpen(false)}
          title={gt('This invoice cannot be accepted yet', {
            context:
              'dialog title when Accept is blocked because the AP invoice is not fully matched',
          })}
          description={
            approvalBlocksIncludeMatchGaps(approvalBlocks)
              ? gt(
                  'Every line has to be matched before this invoice can be accepted.',
                  {
                    context:
                      'dialog body when Accept is blocked on an AP invoice',
                  },
                )
              : gt(
                  'This invoice is missing details required before it can be accepted.',
                  {
                    context:
                      'dialog body when Accept is blocked by a missing supplier or invoice number',
                  },
                )
          }
        >
          <ApprovalBlockList blocks={approvalBlocks} />
        </CannotApproveInvoicesDialog>

        <ConfirmDialog
          open={confirmAcceptOpen}
          onClose={() => setConfirmAcceptOpen(false)}
          onConfirm={handleConfirmAccept}
          title={gt('Confirm Match Acceptance')}
          message={confirmAcceptMessage ?? undefined}
          confirmText={gt('Accept Matches')}
          cancelText={gt('Cancel')}
          loading={acceptInvoice.isPending}
        >
          <TroubleDataAlert
            onHoldCount={onHoldSummary.onHoldCount}
            holdSources={onHoldSummary.holdSources}
            checkFailed={holdsQuery.isError}
          />
        </ConfirmDialog>
      </ResizablePanel>
      <ResizableHandle />
      <ResizablePanel minSize={20}>
        <InvoiceEmailPanel
          inboundEmail={inboundEmail}
          relatedConversations={relatedConversations}
          sourceDocumentId={sourceDocumentId}
          sourceEmailId={sourceEmailId}
        />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}

/**
 * Renders the email side panel for an AP invoice.
 *
 * - 0 conversations: empty state
 * - 1 conversation: a single `SmartEmailViewer` (preserves existing behavior,
 *   including the "attachments" default tab when there's no thread).
 * - 2+ conversations: every conversation is rendered stacked, with the source
 *   thread first. Each thread is wrapped with a small header so the user can
 *   tell them apart.
 */
function InvoiceEmailPanel({
  inboundEmail,
  relatedConversations,
  sourceDocumentId,
  sourceEmailId,
}: {
  inboundEmail: InboundEmail | null | undefined;
  relatedConversations: APInvoiceRelatedConversation[];
  sourceDocumentId: string | undefined;
  sourceEmailId: string | undefined;
}) {
  const gt = useGT();
  const sortedConversations = useMemo(() => {
    // Render the source thread first; everything else in stable order.
    const sorted = [...relatedConversations];
    sorted.sort((a, b) => {
      if (a.isSource && !b.isSource) return -1;
      if (!a.isSource && b.isSource) return 1;
      return 0;
    });
    return sorted;
  }, [relatedConversations]);

  if (sortedConversations.length > 1) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="space-y-4 p-1">
          {sortedConversations.map((conversation) => (
            <div
              key={conversation.conversationId}
              className="rounded-md border bg-background"
            >
              <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {conversation.isSource
                      ? gt('Original Thread', {
                          context:
                            'label for the original email thread that created the invoice',
                        })
                      : gt('Update Thread', {
                          context:
                            'label for a subsequent email thread that updated the invoice',
                        })}
                  </div>
                  <div className="truncate text-sm font-medium">
                    {conversation.subject ||
                      gt('(No subject)', {
                        context: 'fallback when an email has no subject line',
                      })}
                  </div>
                </div>
              </div>
              <div className="p-2">
                <SmartEmailViewer
                  emailData={
                    inboundEmail &&
                    inboundEmail.id === conversation.primaryEmailId
                      ? inboundEmailToEmailData(inboundEmail)
                      : undefined
                  }
                  conversationId={conversation.conversationId}
                  defaultTab="attachments"
                  sourceDocumentId={
                    conversation.isSource ? sourceDocumentId : undefined
                  }
                  sourceEmailId={
                    conversation.isSource ? sourceEmailId : undefined
                  }
                  highlightedDocumentIds={
                    conversation.isSource && sourceDocumentId
                      ? [sourceDocumentId]
                      : undefined
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (inboundEmail) {
    return (
      <SmartEmailViewer
        emailData={inboundEmailToEmailData(inboundEmail)}
        conversationId={inboundEmail.conversationId}
        defaultTab="attachments"
        sourceDocumentId={sourceDocumentId}
        sourceEmailId={sourceEmailId}
        highlightedDocumentIds={
          sourceDocumentId ? [sourceDocumentId] : undefined
        }
      />
    );
  }

  // Exactly one recovered conversation but no eagerly-loaded inboundEmail —
  // still render the thread via SmartEmailViewer. emailData is omitted
  // because the prior `if (inboundEmail)` branch has already handled the
  // truthy case; here EmailThreadViewer fetches the messages itself from
  // conversationId.
  if (sortedConversations.length === 1) {
    const conversation = sortedConversations[0];
    return (
      <SmartEmailViewer
        conversationId={conversation.conversationId}
        defaultTab="attachments"
        sourceDocumentId={conversation.isSource ? sourceDocumentId : undefined}
        sourceEmailId={conversation.isSource ? sourceEmailId : undefined}
        highlightedDocumentIds={
          conversation.isSource && sourceDocumentId
            ? [sourceDocumentId]
            : undefined
        }
      />
    );
  }

  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      <T context="empty state when no email is associated with the invoice">
        No email available
      </T>
    </div>
  );
}
